import _ from 'lodash';
import { useState } from 'react';
import { type PortalSettings as PortalSettingsType } from '@gorilla/portal-shared/src/schemas';
import { useBufferState } from '../hooks/use-buffer-state';
import { BoardDiagnoseModal } from '@gorilla/ui/src/components/BoardDiagnoseModal';
import { checkPortalManagerPermissions } from '@gorilla/portal-shared/src/utils';
import { LANGS, DEFAULT_LANG } from '@gorilla/portal-shared/src/lang';
import { Toggle, Text, TextField, Box, Flex, Button, Label, Tooltip, Icon, Divider, Dropdown } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { getClientPortalUrl } from '../lib/utils';
import { type RegistryEntry, type Portal } from '../services/portal';
import { mondayClient } from '../services/monday';
import { Person, Locked, PersonRound, Bolt, Form, Settings, LogOut, Email, Switch, IPRestrictions } from '@vibe/icons';
import { Heading } from '@vibe/core';
import { RegisterAsPortalManager } from './RegisterAsPortalManager';
import { ConfirmModal } from './ConfirmModal';
import { CustomDomainSettings } from './CustomDomainSettings';
import { MarkdownTextarea } from './MarkdownTextarea';
import { ExternalLink } from './ExternalLink';
import { UploadPortalSettingsButton } from './UploadPortalSettingsButton';

type PortalSettingsProps = {
  portalSettings: PortalSettingsType;
  onChange: (newPortalSettings: PortalSettingsType, forceUiUpdate?: boolean) => void;
  setCustomizationModal: (show: boolean) => void;
  customizationModal: boolean;
  portal: Portal;
  onUnpublishClick: () => void;
  isUnpublishing: boolean;
  clientBoardId: number;
  mondayAccountSlug: string;
  registryEntries?: RegistryEntry[];
  onDiscardDraftClick: () => void;
  isDiscardingDraft: boolean;
  hasUnpublishedChanges: boolean;
  portalManagerPermissions: string[];
  // TODO: Add types
  triggerVerifyCustomDomain: any;
  areRegistryEntriesLoading: any;
  registryEntriesError: any;
  isVerifyingCustomDomain: any;
  verifyCustomDomainError: any;
  triggerConnectCustomDomain: any;
  isConnectCustomDomainConnecting: any;
  customDomainError: any;
  triggerDisconnectCustomDomain: any;
  isConnectCustomDomainDisconnecting: any;
  disconnectCustomDomainError: any;

  screenMode: 'small' | 'medium' | 'large';
  onShowPlaceholdersModalClick: () => void;
};

export function PortalSettings({
  portalSettings,
  onChange,
  portal,
  isUnpublishing,
  onUnpublishClick,
  clientBoardId,
  mondayAccountSlug,
  portalManagerPermissions,
  registryEntries,
  triggerVerifyCustomDomain,
  areRegistryEntriesLoading,
  registryEntriesError,
  isVerifyingCustomDomain,
  verifyCustomDomainError,
  triggerConnectCustomDomain,
  isConnectCustomDomainConnecting,
  customDomainError,
  triggerDisconnectCustomDomain,
  isConnectCustomDomainDisconnecting,
  disconnectCustomDomainError,
  screenMode,
  onShowPlaceholdersModalClick,
}: PortalSettingsProps) {
  const [proposedNewSettings, setProposedNewSettings] = useState<PortalSettingsType | null>(null);
  const [showBoardDiagnoseModal, setShowBoardDiagnoseModal] = useState(false);
  const { bufferState, setBufferState } = useBufferState<PortalSettingsType>(onChange, 700);
  const currentSettings = bufferState || portalSettings;
  const permissionCheck = checkPortalManagerPermissions(portalManagerPermissions);

  function transformLanguageCode(lang: string) {
    let languageLabel = lang;
    if (lang === 'en') {
      languageLabel = 'English';
    } else if (lang === 'de') {
      languageLabel = 'Deutsch';
    } else if (lang === 'fr') {
      languageLabel = 'Français';
    } else if (lang === 'es') {
      languageLabel = 'Español';
    } else if (lang === 'zh') {
      languageLabel = '中文（简体）';
    } else if (lang === 'it') {
      languageLabel = 'Italiano';
    } else if (lang === 'pl') {
      languageLabel = 'Polski';
    } else if (lang === 'pt') {
      languageLabel = 'Português';
    } else if (lang === 'nl') {
      languageLabel = 'Nederlands';
    } else if (lang === 'he') {
      languageLabel = 'עברית' + ' (Experimental)';
    }
    return languageLabel;
  }

  const languageOptions = Object.keys(LANGS).map((lang) => ({
    value: lang,
    label: transformLanguageCode(lang),
  }));

  const selectedLanguageOption = languageOptions.find((option) => option.value === currentSettings.lang) || {
    value: DEFAULT_LANG,
    label: transformLanguageCode(DEFAULT_LANG),
  };

  return (
    <div
      style={{
        overflow: 'hidden',
        overflowY: 'auto',
        height: '100%',
        maxHeight: 'calc(100vh - 73px)',
      }}
    >
      {proposedNewSettings ? (
        <ConfirmModal
          isOpen={true}
          isConfirming={isUnpublishing}
          headline="Overwrite portal settings"
          text="Are you sure you want to overwrite your portal settings?"
          info="By overwriting your portal settings, all existing settings will be replaced with the new ones."
          confirmButtonLabel="Overwrite settings"
          onConfirm={async () => {
            onChange(proposedNewSettings, true);
            setProposedNewSettings(null);
          }}
          onClose={() => {
            setProposedNewSettings(null);
          }}
        />
      ) : null}
      {showBoardDiagnoseModal ? (
        <BoardDiagnoseModal
          boardId={clientBoardId}
          mondayClient={mondayClient}
          onClose={() => {
            setShowBoardDiagnoseModal(false);
          }}
        />
      ) : null}
      <Box>
        <Box
          style={{
            overflow: 'visible',
          }}
        >
          <Box margin="large" marginBottom="large" style={{ overflow: 'visible' }}>
            <Flex direction={screenMode !== 'small' ? 'row' : 'column'} justify="start" align="stretch" gap={24} style={{ width: '100%' }}>
              {/* ----------- LEFT START ----------- */}
              <Flex direction="column" justify="start" align="start" gap={24} style={{ width: screenMode !== 'small' ? '50%' : '100%' }}>
                {/* 
                  ↓↓↓ CLIENT PORTAL ↓↓↓ 
                */}
                <Box
                  border={true}
                  borderColor="layoutBorderColor"
                  rounded="small"
                  backgroundColor="primaryBackgroundColor"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16}>
                      <Flex gap={10} style={{ width: '100%' }}>
                        <div style={{ transform: 'translate3d(0,1px,0)' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 20" width="21" height="18" fill="currentColor">
                            <path
                              fill="currentColor"
                              d="M19.375 0.5C21.0892 0.5 22.4999 1.86724 22.5 3.57129V16.4287C22.4999 18.1328 21.0892 19.5 19.375 19.5H3.625C1.91082 19.5 0.500077 18.1328 0.5 16.4287V3.57129C0.500077 1.86724 1.91082 0.5 3.625 0.5H19.375ZM2.8125 16.4287C2.81258 16.854 3.16545 17.2139 3.625 17.2139H7.0625V7.92871H2.8125V16.4287ZM9.375 17.2139H19.375C19.8346 17.2139 20.1874 16.854 20.1875 16.4287V7.92871H9.375V17.2139ZM2.33887 16.6885C2.34599 16.7226 2.35542 16.756 2.36523 16.7891C2.35468 16.7535 2.34533 16.7174 2.33789 16.6807L2.33887 16.6885ZM20.6455 16.7422C20.6501 16.7242 20.6573 16.7068 20.6611 16.6885L20.6621 16.6807C20.6579 16.7016 20.6508 16.7217 20.6455 16.7422ZM3.625 2.78613C3.16545 2.78613 2.81258 3.146 2.8125 3.57129V5.64258H20.1875V3.57129C20.1874 3.146 19.8346 2.78613 19.375 2.78613H3.625Z"
                            />
                          </svg>
                        </div>
                        <Heading type="h3" weight="bold" align="start">
                          Client Portal
                        </Heading>
                        <Box style={{ marginLeft: 'auto' }}></Box>
                      </Flex>
                      <Text type="text2" color="primary" ellipsis={false}>
                        Give your client portal a <strong>name</strong>, and select a <strong>language</strong> to be used in your portal
                        that matches your monday.com board content best. If you need another portal language that is not listed down below,
                        please don't hesitate to{' '}
                        <ExternalLink href="https://getgorilla.app/contact" target="_blank">
                          contact us
                        </ExternalLink>
                        .
                      </Text>
                    </Flex>
                  </Box>
                  <Divider withoutMargin />
                  <Box padding="xl" paddingTop="large" backgroundColor="greyBackgroundColor">
                    <Flex direction="column" justify="start" align="start" gap={16} style={{ width: '100%' }}>
                      <Flex direction="row" justify="start" align="end" gap={32} style={{ width: '100%' }}>
                        <Box style={{ width: '50%' }}>
                          <TextField
                            title="Portal name"
                            placeholder="Portal name"
                            value={currentSettings.name}
                            size="medium"
                            maxLength={20}
                            onChange={(newName: any) => {
                              setBufferState({ ...currentSettings, name: newName });
                            }}
                          />
                        </Box>
                        <Box style={{ width: '50%' }}>
                          <Text type="text2" color="primary" ellipsis={false} style={{ marginBottom: '6px' }}>
                            Portal language
                          </Text>
                          <div style={{ width: '100%' }}>
                            <Dropdown
                              placeholder="Choose a language"
                              size="medium"
                              clearable={false}
                              searchable={false}
                              value={dropdownValue(selectedLanguageOption)}
                              options={languageOptions}
                              onChange={(newSelectedOption: any) => {
                                if (newSelectedOption) {
                                  setBufferState({ ...currentSettings, lang: newSelectedOption.value });
                                }
                              }}
                              className="custom_dropdown"
                            />
                          </div>
                        </Box>
                      </Flex>
                      <Flex direction="row" justify="start" align="end" gap={32} style={{ width: '100%' }}>
                        <Box style={{ width: '50%' }}>
                          <TextField
                            title="Portal URL"
                            placeholder="Portal URL"
                            value={getClientPortalUrl(portal.id, registryEntries)}
                            size="medium"
                            disabled
                          />
                        </Box>
                        <Box style={{ width: '50%' }}>
                          <TextField title="Portal ID" placeholder="Portal ID" value={portal.id} size="medium" disabled />
                        </Box>
                      </Flex>
                    </Flex>
                  </Box>
                </Box>

                {/* 
                  ↓↓↓ CUSTOM DOMAIN ↓↓↓ 
                */}
                <Box
                  border={true}
                  borderColor="layoutBorderColor"
                  rounded="small"
                  backgroundColor="secondaryBackgroundColor"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <CustomDomainSettings
                    registryEntries={registryEntries}
                    triggerVerifyCustomDomain={triggerVerifyCustomDomain}
                    areRegistryEntriesLoading={areRegistryEntriesLoading}
                    registryEntriesError={registryEntriesError}
                    isVerifyingCustomDomain={isVerifyingCustomDomain}
                    verifyCustomDomainError={verifyCustomDomainError}
                    triggerConnectCustomDomain={triggerConnectCustomDomain}
                    isConnectCustomDomainConnecting={isConnectCustomDomainConnecting}
                    customDomainError={customDomainError}
                    triggerDisconnectCustomDomain={triggerDisconnectCustomDomain}
                    isConnectCustomDomainDisconnecting={isConnectCustomDomainDisconnecting}
                    disconnectCustomDomainError={disconnectCustomDomainError}
                  />
                  <Box
                    paddingStart="xl"
                    paddingEnd="xl"
                    paddingBottom="large"
                    backgroundColor="allgreyBackgroundColor"
                    style={{
                      marginTop: '-8px',
                    }}
                  >
                    <Flex gap={10} style={{ width: '100%' }}>
                      <Box style={{ marginRight: '-4px', marginLeft: '-8px' }}>
                        <Toggle
                          offOverrideText=""
                          onOverrideText=""
                          isSelected={!!currentSettings.redirectSubdomainToCustomDomain}
                          onChange={async (value: any) => {
                            setBufferState({ ...currentSettings, redirectSubdomainToCustomDomain: value });
                          }}
                          size="small"
                          disabled={!registryEntries?.find((e: any) => e.kind === 'custom' && e.verification_status === 'active')}
                        />
                      </Box>
                      <Text
                        type="text2"
                        color={
                          !registryEntries?.find((e: any) => e.kind === 'custom' && e.verification_status === 'active')
                            ? 'secondary'
                            : 'primary'
                        }
                        ellipsis={true}
                      >
                        Redirect the portal's default subdomain to your connected custom domain.
                      </Text>
                    </Flex>
                  </Box>
                </Box>

                {/*
                  ↓↓↓ CACHE ↓↓↓
                */}
                <Box
                  border={true}
                  borderColor="layoutBorderColor"
                  rounded="small"
                  backgroundColor="secondaryBackgroundColor"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16} style={{ height: '100%' }}>
                      <Flex gap={6} style={{ width: '100%' }}>
                        <Icon icon={Bolt} size="26" style={{ marginLeft: '-4px', transform: 'translate3d(0,0,0)' }} />
                        <Heading type="h3" weight="bold" align="start">
                          Cache
                        </Heading>
                        <Box style={{ marginRight: '-8px', marginLeft: 'auto' }}>
                          <Toggle
                            offOverrideText=""
                            onOverrideText=""
                            isSelected={!!currentSettings.enableCache}
                            onChange={async (value: any) => {
                              setBufferState({ ...currentSettings, enableCache: value });
                            }}
                            size="small"
                          />
                        </Box>
                      </Flex>
                      <Text type="text2" color="primary" ellipsis={false}>
                        When enabled, your{' '}
                        <ExternalLink
                          href="https://getgorilla.app/products/client-portal/docs/getting-started/clients-board"
                          target="_blank"
                        >
                          Clients Board
                        </ExternalLink>{' '}
                        data is cached to improve loading performance. Pages with board sections may load faster, but{' '}
                        <strong>recent changes</strong> to the <strong>Clients Board</strong> can take up to <strong>3 minutes</strong> to
                        appear for clients. Preview mode is not affected by this cache.
                      </Text>
                    </Flex>
                  </Box>
                </Box>

                {/*
                  ↓↓↓ BACKUP ↓↓↓
                */}
                <Box
                  border={true}
                  borderColor="layoutBorderColor"
                  rounded="small"
                  backgroundColor="secondaryBackgroundColor"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16}>
                      <Flex gap={10} style={{ width: '100%' }}>
                        <Icon icon={Settings} size="24" style={{ marginLeft: '-2px', transform: 'translate3d(0,-1px,0)' }} />
                        <Heading type="h3" weight="bold" align="start">
                          Portal Settings
                        </Heading>
                        <Box style={{ marginLeft: 'auto' }}></Box>
                      </Flex>
                      <Text type="text2" color="primary" ellipsis={false}>
                        Export and download the current <strong>client portal settings</strong> to your local computer to create a backup,
                        or to quickly <strong>migrate portal settings</strong> into another client portal. Importing only works for portals
                        on the same monday.com account.
                      </Text>
                    </Flex>
                  </Box>
                  <Divider withoutMargin />
                  <Box backgroundColor="greyBackgroundColor">
                    <Flex gap={0} align="stretch" style={{ width: '100%' }}>
                      <Box padding="xl" paddingTop="large" paddingEnd="large" paddingBottom="large">
                        <Button
                          leftIcon={LogOut}
                          kind="primary"
                          size="medium"
                          color="primary"
                          onClick={() => {
                            const blob = new Blob([JSON.stringify(currentSettings, null, 2)], { type: 'application/json' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `portal-settings-${portal?.id}.json`;
                            a.click();
                          }}
                        >
                          Export
                        </Button>
                      </Box>
                      <div style={{ display: 'block' }}>
                        <Divider direction="vertical" withoutMargin />
                      </div>
                      <Box padding="xl" paddingTop="large" paddingBottom="large" paddingStart="large" style={{ flex: 1 }}>
                        <UploadPortalSettingsButton onUploaded={(newPortalSettings) => setProposedNewSettings(newPortalSettings)} />
                      </Box>
                    </Flex>
                  </Box>
                </Box>

                {/* 
                  ↓↓↓ INTEGRATION USER ↓↓↓ 
                */}
                <Box
                  border={true}
                  borderColor="layoutBorderColor"
                  rounded="small"
                  backgroundColor="secondaryBackgroundColor"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16}>
                      <Flex gap={10}>
                        <Icon icon={PersonRound} size="24" style={{ marginLeft: '-4px', transform: 'translate3d(0,-1px,0)' }} />
                        <Heading type="h3" weight="bold" align="start">
                          Integration User
                        </Heading>
                        {!permissionCheck.hasAllPermissions ? (
                          <Tooltip
                            content={`Authorize now to access ${permissionCheck
                              .featuresWithMissingPermissions()
                              .map((feature) =>
                                feature
                                  .split('-')
                                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(' '),
                              )
                              .join(', ')}`}
                          >
                            <Label text="Missing permissions" className="custom__label--draft___" color="negative" kind="fill" />
                          </Tooltip>
                        ) : null}
                      </Flex>
                      <Text type="text2" color="primary" ellipsis={false}>
                        The app requires a <strong>permanently assigned</strong> monday.com user account to{' '}
                        <strong>fetch, display and modify board data</strong> from across your monday.com account. This{' '}
                        <ExternalLink
                          href="https://getgorilla.app/products/client-portal/docs/features/settings#anchor-7482"
                          target="_blank"
                        >
                          integration user
                        </ExternalLink>{' '}
                        must be the board owner of the{' '}
                        <ExternalLink
                          href={`https://${mondayAccountSlug}.monday.com/boards/${clientBoardId}/board_subscribers`}
                          target="_blank"
                        >
                          Clients Board
                        </ExternalLink>
                        . <strong>Authorize</strong>, if you either do need to reassign the integration user to your monday.com user
                        account, or to grant the app new permissions on request.
                      </Text>
                    </Flex>
                  </Box>
                  <Divider withoutMargin />
                  <Box padding="xl" paddingTop="large" backgroundColor="greyBackgroundColor">
                    <Flex
                      gap={16}
                      direction="row"
                      align="end"
                      style={{
                        marginTop: 'auto',
                        width: '100%',
                      }}
                    >
                      <Box style={{ flex: 1, width: '100%' }}>
                        <TextField
                          title="Assigned user"
                          value={_.get(portal, 'portalManager.name') || 'Not assigned'}
                          size="medium"
                          icon={Person}
                          //readonly
                          disabled
                        />
                      </Box>
                      <Box>
                        <RegisterAsPortalManager boardId={clientBoardId} slug={mondayAccountSlug} />
                      </Box>
                    </Flex>
                    {!permissionCheck.hasAllPermissions ? (
                      <Text type="text3" weight="normal" color="negative" ellipsis={false} style={{ marginTop: '12px' }}>
                        The app is missing permissions to access new features. Reauthorize now to access{' '}
                        <strong>
                          {permissionCheck
                            .featuresWithMissingPermissions()
                            .map((feature) =>
                              feature
                                .split('-')
                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' '),
                            )
                            .join(', ')}
                        </strong>
                        .
                      </Text>
                    ) : null}
                  </Box>
                </Box>
              </Flex>
              {/* ------------ LEFT END ------------ */}

              {/* ---------- RIGHT START ----------- */}
              <Flex direction="column" justify="start" align="start" gap={24} style={{ width: screenMode !== 'small' ? '50%' : '100%' }}>
                {/* 
                  ↓↓↓ LOGIN TEXT ↓↓↓ 
                */}
                <Box
                  border={true}
                  borderColor="layoutBorderColor"
                  rounded="small"
                  backgroundColor="secondaryBackgroundColor"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16}>
                      <Flex gap={10} style={{ width: '100%' }}>
                        <Icon icon={Form} size="24" style={{ marginLeft: '-2px', transform: 'translate3d(0,-1px,0)' }} />
                        <Heading type="h3" weight="bold" align="start">
                          Login Text
                        </Heading>
                        <Box style={{ marginLeft: 'auto' }}></Box>
                      </Flex>
                      <Text type="text2" color="primary" ellipsis={false}>
                        Optionally display a short, <strong>custom text on the login page</strong> of your client portal. For example,
                        welcome your clients and give context. Advanced usage includes showing a link to a{' '}
                        <ExternalLink href="https://getgorilla.app/products/client-portal/docs/user-guides/client-access#anchor-7827">
                          Registration{' '}
                        </ExternalLink>
                        or{' '}
                        <ExternalLink href="https://getgorilla.app/products/client-portal/docs/user-guides/resetting-passwords#anchor-7570">
                          Reset Password{' '}
                        </ExternalLink>
                        monday.com WorkForm.
                      </Text>
                    </Flex>
                  </Box>
                  <Divider withoutMargin />
                  <Box padding="xl" paddingTop="large" backgroundColor="greyBackgroundColor">
                    <MarkdownTextarea
                      rows={4}
                      hideHelp={false}
                      value={currentSettings?.loginPage?.introText || ''}
                      label="Text"
                      onChange={(value) => {
                        setBufferState({ ...currentSettings, loginPage: { ...(currentSettings.loginPage || {}), introText: value } });
                      }}
                      onShowPlaceholdersModalClick={onShowPlaceholdersModalClick}
                    />
                  </Box>
                </Box>

                {/* 
                  ↓↓↓ USER PASSWORD ↓↓↓ 
                */}
                <Box
                  border={true}
                  borderColor="layoutBorderColor"
                  rounded="small"
                  backgroundColor="secondaryBackgroundColor"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16} style={{ height: '100%' }}>
                      <Flex gap={10} style={{ width: '100%' }}>
                        <Icon icon={Locked} size="24" style={{ marginLeft: '-4px', transform: 'translate3d(0,-1px,0)' }} />
                        <Heading type="h3" weight="bold" align="start">
                          Password Update
                        </Heading>
                        {!permissionCheck.hasAllPermissions ? (
                          <Tooltip
                            content={`Authorize now to access ${permissionCheck
                              .featuresWithMissingPermissions()
                              .map((feature) =>
                                feature
                                  .split('-')
                                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(' '),
                              )
                              .join(', ')}`}
                          >
                            <Label text="Missing permissions" className="custom__label--draft___" color="negative" kind="fill" />
                          </Tooltip>
                        ) : null}
                        <Box style={{ marginRight: '-8px', marginLeft: 'auto' }}>
                          <Toggle
                            offOverrideText=""
                            onOverrideText=""
                            isSelected={!!currentSettings.allowUpdatePassword}
                            onChange={async (value: any) => {
                              setBufferState({ ...currentSettings, allowUpdatePassword: value });
                            }}
                            size="small"
                          />
                        </Box>
                      </Flex>
                      <Text type="text2" color="primary" ellipsis={false}>
                        Allow clients, signed in with a <strong>credentials login method</strong>, to update their password directly in the
                        client portal. When enabled, clients can access their{' '}
                        <Icon icon={Settings} size={15} style={{ transform: 'translate3d(0,2px,0)' }} /> <strong>Account Settings</strong>{' '}
                        by clicking their initials and change the password on their own behalf.
                      </Text>
                    </Flex>
                  </Box>
                </Box>

                {/*
                  ↓↓↓ CLIENT INBOX ↓↓↓
                */}
                <Box
                  border={true}
                  borderColor="layoutBorderColor"
                  rounded="small"
                  backgroundColor="secondaryBackgroundColor"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16} style={{ height: '100%' }}>
                      <Flex gap={10} style={{ width: '100%' }}>
                        <Icon icon={Email} size="24" style={{ marginLeft: '-4px', transform: 'translate3d(0,0,0)' }} />
                        <Heading type="h3" weight="bold" align="start">
                          Client Inbox
                        </Heading>
                        <Box style={{ marginRight: '-8px', marginLeft: 'auto' }}>
                          <Toggle
                            offOverrideText=""
                            onOverrideText=""
                            isSelected={!!currentSettings.enableClientInbox}
                            onChange={async (value: any) => {
                              setBufferState({ ...currentSettings, enableClientInbox: value });
                            }}
                            size="small"
                          />
                        </Box>
                      </Flex>
                      <Text type="text2" color="primary" ellipsis={false}>
                        Allow clients to exchange <strong>private messages</strong> with your team on a global level. When enabled, clients
                        can access their <Icon icon={Email} size={15} style={{ transform: 'translate3d(0,2.5px,0)' }} />{' '}
                        <strong>Client Inbox</strong> in the portal across all pages. These messages are stored on each client item as item
                        updates and can be sent and replied to by your team in the{' '}
                        <ExternalLink
                          href="https://getgorilla.app/products/client-portal/docs/getting-started/app-views/client-comments-view"
                          target="_blank"
                        >
                          Client Comments
                        </ExternalLink>{' '}
                        item view on your{' '}
                        <ExternalLink
                          href="https://getgorilla.app/products/client-portal/docs/getting-started/clients-board"
                          target="_blank"
                        >
                          Clients Board
                        </ExternalLink>
                        .
                      </Text>
                    </Flex>
                  </Box>
                </Box>

                {/*
                  ↓↓↓ GOOGLE AUTH ↓↓↓
                */}
                <Box
                  border={true}
                  borderColor="layoutBorderColor"
                  rounded="small"
                  backgroundColor="primaryBackgroundColor"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16}>
                      <Flex gap={10} style={{ width: '100%' }}>
                        <div style={{ transform: 'translate3d(0,1px,0)' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512" width="18" height="18" fill="currentColor">
                            <path
                              d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
                              fill="currentColor"
                            />
                          </svg>
                        </div>
                        <Heading type="h3" weight="bold" align="start">
                          Google Authentication
                        </Heading>
                        <Box style={{ marginRight: '-8px', marginLeft: 'auto' }}>
                          <Toggle
                            offOverrideText=""
                            onOverrideText=""
                            isSelected={!!currentSettings.enableGoogleAuth}
                            onChange={async (value: any) => {
                              setBufferState({ ...currentSettings, enableGoogleAuth: value });
                            }}
                            size="small"
                          />
                        </Box>
                      </Flex>
                      <Text type="text2" color="primary" ellipsis={true} maxLines={3} style={{ maxWidth: '570px' }}>
                        Enable Google authentication in your client portal, making it possible for your clients to sign in with their{' '}
                        <strong>Google account</strong> instead of credentials. It is mandatory to have a{' '}
                        <Icon icon={IPRestrictions} size={15} style={{ transform: 'translate3d(0,2px,0)' }} />
                        &nbsp;<strong>Custom Domain</strong> connected. Learn how to set up{' '}
                        <ExternalLink
                          href="https://getgorilla.app/products/client-portal/docs/user-guides/google-authentication"
                          target="_blank"
                        >
                          Google Logins in your portal
                        </ExternalLink>
                        .
                      </Text>
                    </Flex>
                  </Box>
                  <Divider withoutMargin />
                  <Box padding="xl" paddingTop="large" backgroundColor="greyBackgroundColor">
                    <Box>
                      <Box style={{ position: 'relative' }}>
                        {currentSettings.enableGoogleAuth ? (
                          <Text
                            type="text3"
                            color="secondary"
                            ellipsis={false}
                            maxLines={1}
                            style={{ position: 'absolute', top: '4px', right: '0px' }}
                          >
                            Required
                          </Text>
                        ) : null}
                        <TextField
                          title="OAuth client ID"
                          value={currentSettings.googleClientId}
                          onChange={(newGoogleClientId) => {
                            setBufferState({ ...currentSettings, googleClientId: newGoogleClientId });
                          }}
                          placeholder="e.g. xxxxx-xxxxx.apps.googleusercontent.com"
                          size="medium"
                          disabled={!currentSettings.enableGoogleAuth}
                          autoFocus={currentSettings.enableGoogleAuth && !currentSettings.googleClientId}
                        />
                      </Box>
                    </Box>
                    {/* 
                    <Box style={{ marginTop: '16px' }}>
                      <Box style={{ position: 'relative' }}>
                        {portalSettings.enableGoogleAuth ? (
                          <Text
                            type="text3"
                            color="secondary"
                            ellipsis={false}
                            maxLines={1}
                            style={{ zIndex: 100, position: 'absolute', top: '4px', right: '0px' }}
                          >
                            Need this feature?{' '}
                            <a href="https://getgorilla.app/contact" target="_blank">
                              Let us know
                            </a>
                          </Text>
                        ) : null}

                        <TextField
                          title="Workspace domain"
                          value={portalSettings.googleWorkspaceDomain}
                          onChange={(newDomain) => {
                            onChange({ ...portalSettings, googleWorkspaceDomain: newDomain });
                          }}
                          placeholder="e.g. domain.com"
                          size="medium"
                          //disabled={!portalSettings.enableGoogleAuth || !portalSettings.googleClientId}
                          disabled
                        />
                      </Box>
                    </Box>
                    */}
                  </Box>
                </Box>

                {/* 
                  ↓↓↓ GOOGLE ANALYTICS ↓↓↓ 
                */}
                <Box
                  border={true}
                  borderColor="layoutBorderColor"
                  rounded="small"
                  backgroundColor="primaryBackgroundColor"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16}>
                      <Flex gap={10} style={{ width: '100%' }}>
                        <div style={{ transform: 'translate3d(0,1px,0)' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512" width="18" height="18" fill="currentColor">
                            <path
                              d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
                              fill="currentColor"
                            />
                          </svg>
                        </div>
                        <Heading type="h3" weight="bold" align="start">
                          Google Analytics
                        </Heading>
                      </Flex>
                      <Text type="text2" color="primary" ellipsis={false}>
                        Start tracking user interactions with Google Analytics to gain <strong>advanced insights</strong> into user
                        behavior. Enable <strong>Google Analytics</strong> in the client portal by pasting your GA4 Measurement ID below.
                        This feature involves <strong>third-party cookies</strong> created and handled by Google.
                      </Text>
                    </Flex>
                  </Box>
                  <Divider withoutMargin />
                  <Box padding="xl" paddingTop="large" backgroundColor="greyBackgroundColor">
                    <TextField
                      title="Measurement ID"
                      value={currentSettings.googleAnalyticsId || ''}
                      onChange={(newGoogleAnalyticsId) => {
                        setBufferState({ ...currentSettings, googleAnalyticsId: newGoogleAnalyticsId });
                      }}
                      placeholder="e.g. G-XXXXXXXXXX"
                      size="medium"
                    />
                  </Box>
                </Box>

                <Box
                  border={true}
                  borderColor="layoutBorderColor"
                  rounded="small"
                  backgroundColor="secondaryBackgroundColor"
                  shadow="xs"
                  style={{
                    width: '100%',
                  }}
                >
                  <Box padding="xl" paddingTop="large" paddingBottom="large">
                    <Flex direction="column" justify="start" align="start" gap={16} style={{ height: '100%' }}>
                      <Flex gap={10} style={{ width: '100%' }}>
                        <Icon icon={Switch} size="22" style={{ marginLeft: '-4px', transform: 'translate3d(0,0,0)' }} />
                        <Heading type="h3" weight="bold" align="start">
                          Automatic Mismatch Handling
                        </Heading>
                        <Box style={{ marginRight: '-8px', marginLeft: 'auto' }}>
                          <Toggle
                            offOverrideText=""
                            onOverrideText=""
                            isSelected={!!currentSettings.enableMismatchHandling}
                            onChange={async (value: any) => {
                              setBufferState({ ...currentSettings, enableMismatchHandling: value });
                            }}
                            size="small"
                          />
                        </Box>
                      </Flex>
                      <Text type="text2" color="primary" ellipsis={false}>
                        This feature only applies to{' '}
                        <ExternalLink
                          href="https://getgorilla.app/products/client-portal/docs/user-guides/board-setup#anchor-6929"
                          target="_blank"
                        >
                          dedicated client board
                        </ExternalLink>{' '}
                        sections. When associated boards change after setup, such as adding new columns, this option attempts to
                        automatically map columns between <strong>Template boards</strong> and <strong>Dedicated client boards</strong>{' '}
                        based on their names and types, helping resolve structural mismatches.
                      </Text>
                    </Flex>
                  </Box>
                </Box>
              </Flex>
              {/* ------------ RIGHT END ----------- */}
            </Flex>
          </Box>
        </Box>
      </Box>
    </div>
  );
}
